'use client';

import { cn } from '@/lib/utils';

export type VisualizerType = 'spectrum' | 'particle' | 'character';
export type TrackInfoStyle = 'off' | 'simple' | 'player';

export interface VideoLivePreviewProps {
  /** 9:16 (짧은 영상) / 16:9 (긴 영상) */
  aspect: '9:16' | '16:9';
  color: string;
  /** 0–100 (시각화 세로 위치) */
  posY: number;
  /** 10–200 (시각화 가로 너비 %) */
  spectrumWidth: number;
  visualizerType: VisualizerType;
  /** 긴 영상 전용 — 'off' | 'simple' | 'player' */
  trackInfoStyle?: TrackInfoStyle;
  /** 트랙 제목 미리보기 텍스트 */
  trackTitle?: string;
  /** 짧은 영상 전용 — 하단 타이틀 pill */
  showTitle?: boolean;
}

/**
 * 영상 생성 옵션의 즉시 미리보기 (SVG mock).
 *
 * 옵션이 바뀌는 그 즉시 React 리렌더로 갱신되며, 백엔드/sidecar 호출 없이
 * 클라이언트에서 시각화 핵심 요소만 mock으로 그린다. 실제 영상은 음악·ffmpeg
 * 렌더 결과와 다를 수 있다.
 */
export function VideoLivePreview({
  aspect,
  color,
  posY,
  spectrumWidth,
  visualizerType,
  trackInfoStyle = 'off',
  trackTitle = '예시 트랙 — 미리보기',
  showTitle = false,
}: VideoLivePreviewProps) {
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-lg border border-neutral-200',
        'bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900',
        aspect === '9:16' ? 'aspect-[9/16] max-w-[280px] mx-auto' : 'aspect-video'
      )}
    >
      <Visualizer type={visualizerType} color={color} posY={posY} width={spectrumWidth} />

      {trackInfoStyle === 'simple' && (
        <div className="absolute top-4 left-4 right-4 px-3 py-2 rounded bg-black/40 text-white">
          <p className="text-xs sm:text-sm truncate">{trackTitle}</p>
        </div>
      )}

      {trackInfoStyle === 'player' && (
        <div className="absolute top-4 left-4 right-4 p-3 rounded-lg bg-black/60 backdrop-blur-sm border border-white/20 text-white">
          <p className="text-xs sm:text-sm truncate font-medium">{trackTitle}</p>
          <div className="mt-2 flex items-center gap-2">
            <span aria-hidden className="text-white/70 text-xs">⏮</span>
            <span aria-hidden className="text-white text-sm">▶</span>
            <span aria-hidden className="text-white/70 text-xs">⏭</span>
            <div className="flex-1 h-1 rounded bg-white/20 overflow-hidden">
              <div className="h-full bg-white/80" style={{ width: '35%' }} />
            </div>
          </div>
        </div>
      )}

      {showTitle && (
        <div
          className="absolute left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-medium whitespace-nowrap"
          style={{ top: '72%' }}
        >
          {trackTitle}
        </div>
      )}

      <span className="absolute top-2 right-2 text-[10px] text-white/40 px-2 py-0.5 rounded bg-black/30">
        미리보기
      </span>
    </div>
  );
}

interface VisualizerProps {
  type: VisualizerType;
  color: string;
  posY: number;
  width: number;
}

function Visualizer({ type, color, posY, width }: VisualizerProps) {
  if (type === 'spectrum') {
    const bars = [40, 65, 35, 80, 55, 90, 45, 70, 30];
    return (
      <div
        className="absolute left-1/2 flex items-end gap-1"
        style={{
          top: `${posY}%`,
          height: '22%',
          width: `${Math.min(Math.max(width, 10), 200)}%`,
          maxWidth: '100%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t"
            style={{ height: `${h}%`, backgroundColor: color, opacity: 0.9 }}
          />
        ))}
      </div>
    );
  }

  if (type === 'particle') {
    const dots = Array.from({ length: 28 }, (_, i) => ({
      x: ((i * 37) % 100 + (i * 13) % 7) % 100,
      y: ((i * 53) % 100 + (i * 19) % 11) % 100,
      r: 1.5 + (i % 4),
      o: 0.4 + ((i % 5) * 0.12),
    }));
    return (
      <svg className="absolute inset-0 w-full h-full" aria-hidden>
        {dots.map((d, i) => (
          <circle
            key={i}
            cx={`${d.x}%`}
            cy={`${d.y}%`}
            r={d.r}
            fill={color}
            opacity={d.o}
          />
        ))}
      </svg>
    );
  }

  // character — 단순 silhouette
  return (
    <div
      className="absolute left-1/2"
      style={{
        top: `${posY}%`,
        transform: 'translate(-50%, -50%)',
        width: '38%',
        aspectRatio: '1 / 1.3',
      }}
    >
      <svg viewBox="0 0 100 130" className="w-full h-full" aria-hidden>
        <circle cx="50" cy="30" r="22" fill={color} opacity={0.85} />
        <path
          d="M 50 55 Q 25 60 25 100 L 25 125 L 75 125 L 75 100 Q 75 60 50 55 Z"
          fill={color}
          opacity={0.6}
        />
      </svg>
    </div>
  );
}
